import { useCallback, useEffect, useState } from "react";
import { ArrowRight, Plus, UserCircle, Users } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/** Named respondents (SLT members) for a multi-respondent element such as Leadership
 *  Effectiveness. Every member answers the same survey; the element score is the
 *  equal-weight average of all members. */
export const SltRespondents = ({ framework, element, period, monthLabel, onPick,
                                 ctaLabel = "Start assessment" }) => {
  const { tenantId } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/tenants/${tenantId}/surveys/${framework}/respondents`,
        { params: { element, ...(period ? { period } : {}) } });
      setData(res.data);
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId, framework, element, period]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return;
    setAdding(true);
    try {
      await api.post(`/tenants/${tenantId}/surveys/${framework}/respondents`,
        { name: form.name, email: form.email || null, element_code: element });
      setForm({ name: "", email: "" });
      toast.success("SLT member added");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setAdding(false); }
  };

  if (!data) return <p className="label-caps animate-pulse" data-testid="slt-loading">Loading respondents</p>;
  if (data.error) return <p className="text-sm text-muted-foreground" data-testid="slt-error">{data.error}</p>;

  return (
    <div className="space-y-6" data-testid="slt-respondents">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-caps text-goldink">Senior leadership team</p>
          <h2 className="mt-1 font-display text-2xl text-navy">Who is answering?</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Every SLT member answers this survey separately{monthLabel ? ` for ${monthLabel}` : ""}.
            The element score is the average of all members.
          </p>
        </div>
        <div className="rounded-xl border border-border bg-muted/40 px-5 py-3 text-right">
          <p className="label-caps">Averaged score</p>
          <p className="font-mono text-2xl tabular-nums text-navy" data-testid="slt-average">
            {data.average10 ?? "—"}<span className="text-sm text-muted-foreground">/10</span>
          </p>
        </div>
      </div>

      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        <div className="hidden grid-cols-[1.6fr_1.4fr_0.8fr_0.7fr_auto] gap-3 bg-muted/40 px-5 py-3 text-[9px] uppercase tracking-[0.12em] text-muted-foreground md:grid">
          <span>SLT name</span><span>Email</span><span>Answered</span><span>Score /10</span><span />
        </div>
        {data.respondents.map((r) => (
          <div key={r.id} data-testid={`slt-row-${r.id}`}
               className="grid grid-cols-1 gap-2 px-5 py-4 transition-colors hover:bg-sky/10 md:grid-cols-[1.6fr_1.4fr_0.8fr_0.7fr_auto] md:items-center md:gap-3">
            <span className="flex items-center gap-2 text-sm font-medium text-navy">
              <UserCircle size={20} className="text-deepblue" /> {r.name}
            </span>
            <span className="truncate text-xs text-muted-foreground">{r.email || "—"}</span>
            <span className="font-mono text-xs tabular-nums text-foreground/80">
              {r.answered}/{r.total}
            </span>
            <span className="font-mono text-sm tabular-nums text-navy">
              {r.score10 ?? <span className="text-xs text-muted-foreground">Not yet assessed</span>}
            </span>
            <Button data-testid={`slt-pick-${r.id}`} onClick={() => onPick(r.key, r)}
                    className="h-9 rounded-full text-[10px] uppercase tracking-[0.16em]">
              {r.submitted ? "Review answers" : ctaLabel}
              <ArrowRight size={13} className="ml-1.5" />
            </Button>
          </div>
        ))}
        {data.respondents.length === 0 && (
          <div className="flex items-center gap-3 px-5 py-8 text-sm text-muted-foreground"
               data-testid="slt-empty">
            <Users size={22} className="text-deepblue" />
            No SLT member added yet — add the first one below.
          </div>
        )}
      </div>

      <div className="rounded-xl border border-dashed border-border p-5" data-testid="slt-add">
        <p className="label-caps">Add an SLT member</p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <div className="space-y-2">
            <Label className="label-caps">Name</Label>
            <Input data-testid="slt-name-input" value={form.name} placeholder="e.g. Kalliyammal"
                   onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Email (for score updates)</Label>
            <Input data-testid="slt-email-input" type="email" value={form.email}
                   placeholder="name@company.com"
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <Button data-testid="slt-add-submit" disabled={!form.name.trim() || adding} onClick={add}
                  className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">
            <Plus size={13} className="mr-1.5" /> Add member
          </Button>
        </div>
      </div>
    </div>
  );
};
